import React from "react";
import ScrollToTop from "react-scroll-up";
import {
  FiArrowRight,
  FiChevronUp,
  FiCpu,
  FiExternalLink,
  FiLayers,
  FiTerminal,
  FiZap,
} from "react-icons/fi";
import Helmet from "../component/common/Helmet";
import TextLoop from "react-text-loop";
import HeaderThree from "../component/header/HeaderThree";
import FooterTwo from "../component/footer/FooterTwo";
import TabTwo from "../elements/tab/TabTwo";
import ContactThree from "../elements/contact/ContactThree";
import PortfolioList from "../elements/portfolio/PortfolioList";
import ServiceList from "../elements/service/ServiceList";
import BlogContent from "../elements/blog/BlogContent";

import about from "../assets/images/about/me2.jpg";
import contactImg from "../assets/images/about/paulOlleSoftwareEngineer.png";

const SlideList = [
  {
    textPosition: "text-left",
    category: "",
    description: "",
    buttonText: "",
    buttonLink: "",
  },
];

const HeroSignals = [
  {
    icon: FiCpu,
    label: "AI workflows",
    value: "Agents, skills, automation",
  },
  {
    icon: FiLayers,
    label: "Interactive web",
    value: "React, Next.js, Three.js",
  },
  {
    icon: FiZap,
    label: "Delivery mode",
    value: "Fast, polished, pragmatic",
  },
];

const HeroRoles = [
  " a Software Engineer",
  " an AI Workflow Builder",
  " a React / Next.js Dev",
  " a Three.js Dev",
  " a Product Builder",
  " a React Native Dev",
  " a D3.js Data Viz Dev",
  " a Gameplay Developer",
];

const HeroConsoleRows = [
  {
    label: "Current focus",
    value: "AI products and interactive web apps",
  },
  {
    label: "Stack",
    value: "React / Next.js / Node / Prisma / Vercel",
  },
  {
    label: "Available for",
    value: "Freelance product builds and agent workflows",
  },
];

const AboutHighlights = [
  {
    number: "01",
    title: "Product feel",
    text: "Interfaces built for clarity, motion, and repeated daily use.",
  },
  {
    number: "02",
    title: "AI leverage",
    text: "Codex skills, agents, and automations where they improve delivery.",
  },
  {
    number: "03",
    title: "Technical range",
    text: "Web apps, 3D scenes, data viz, mobile prototypes, Web3, and games.",
  },
];

const SkillStack = [
  "React",
  "Next.js",
  "TypeScript",
  "React Native",
  "Three.js",
  "D3.js",
  "Node.js",
  "Prisma",
  "Vercel",
  "Codex",
  "Claude",
  "OpenAI",
  "n8n",
  "Unity",
  "C#",
  "Expo",
  "Solidity",
  "Ethers.js",
  "Web3",
  "Ethereum",
];

const PortfolioLanding = () => {
  const heroSignalRowRef = React.useRef(null);

  React.useEffect(() => {
    const root = document.querySelector(".portfolio-refresh");

    if (!root) {
      return undefined;
    }

    const handleAnchorClick = (event) => {
      if (event.defaultPrevented || !event.target.closest) {
        return;
      }

      const anchor = event.target.closest('a[href^="#"]');

      if (!anchor) {
        return;
      }

      const href = anchor.getAttribute("href");
      const target = href && document.querySelector(href);

      if (!target) {
        return;
      }

      event.preventDefault();
      document.querySelector(".header-wrapper")?.classList.remove("menu-open");
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.pushState(null, "", href);
    };

    root.addEventListener("click", handleAnchorClick);

    return () => {
      root.removeEventListener("click", handleAnchorClick);
    };
  }, []);

  React.useEffect(() => {
    const row = heroSignalRowRef.current;

    if (!row || typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    let timerId;
    let resumeTimerId;
    let currentIndex = 0;

    const getCards = () => Array.from(row.querySelectorAll(".hero-signal"));

    const scrollToCard = (index, behavior = "smooth") => {
      const cards = getCards();

      if (!cards.length) {
        return;
      }

      const card = cards[index % cards.length];
      const rowStyles = window.getComputedStyle(row);
      const leftPadding = parseFloat(rowStyles.paddingLeft) || 0;

      currentIndex = index % cards.length;
      row.scrollTo({
        left: Math.max(0, card.offsetLeft - row.offsetLeft - leftPadding),
        behavior,
      });
    };

    const stop = () => {
      window.clearInterval(timerId);
      timerId = undefined;
    };

    const start = () => {
      stop();

      if (!mediaQuery.matches || getCards().length < 2) {
        return;
      }

      timerId = window.setInterval(() => {
        scrollToCard(currentIndex + 1);
      }, 3200);
    };

    const pauseThenResume = () => {
      stop();
      window.clearTimeout(resumeTimerId);

      if (mediaQuery.matches) {
        resumeTimerId = window.setTimeout(start, 5600);
      }
    };

    const handleMediaChange = () => {
      if (mediaQuery.matches) {
        scrollToCard(0, "auto");
        start();
      } else {
        stop();
        row.scrollTo({ left: 0, behavior: "auto" });
      }
    };

    row.addEventListener("pointerdown", pauseThenResume);
    row.addEventListener("focusin", pauseThenResume);
    mediaQuery.addEventListener("change", handleMediaChange);

    handleMediaChange();

    return () => {
      stop();
      window.clearTimeout(resumeTimerId);
      row.removeEventListener("pointerdown", pauseThenResume);
      row.removeEventListener("focusin", pauseThenResume);
      mediaQuery.removeEventListener("change", handleMediaChange);
    };
  }, []);

  let title = "About Me",
    description = `I build modern web products with React, TypeScript, Next.js, Node.js, Prisma, Vercel, Three.js, and AI-assisted workflows. My recent work spans MATIS art-tech experiences, management and leadership platforms, internal enterprise tools, custom Codex skills, Web3 experiments, and agent-based job-search automation.

I have 8 years of experience across freelance delivery, large-scale client projects, Olympe.io, hospital UX, mobile apps, and game development. I care about polished UI, reliable architecture, pragmatic delivery, and using AI tools where they genuinely accelerate the work.`;
  const PostList = BlogContent.slice(0, 3);
  return (
    <div className="active-dark portfolio-refresh">
      <Helmet pageTitle="Paul Olle | Software Engineer" />

      <HeaderThree homeLink="/" logo="symbol-dark" color="color-black" />
      {/* Start Slider Area   */}
      <div id="home" className="fix">
        <div className="slider-wrapper overlay">
          {/* Start Single Slide */}
          {SlideList.map((value, index) => (
            <div
              className="slide personal-portfolio-slider portfolio-hero slider-paralax slider-style-3 d-flex align-items-center justify-content-center bg_image bg_image--25"
              key={index}
            >
              <div className="portfolio-hero-overlay" aria-hidden="true" />
              <div className="portfolio-hero-grid" aria-hidden="true" />
              <div className="container portfolio-hero-container">
                <div className="row align-items-center">
                  <div className="col-lg-7">
                    <div className={`inner portfolio-hero-copy ${value.textPosition}`}>
                      {value.category ? (
                        <span className="section-kicker hero-kicker">{value.category}</span>
                      ) : (
                        ""
                      )}
                      <h1 className="title">
                        Hi, I’m Paul Olle, <br />
                        <TextLoop className="hero-role-loop hero-role-loop--desktop" mask>
                          {HeroRoles.map((role) => (
                            <span key={role}>{role}</span>
                          ))}
                        </TextLoop>
                        <TextLoop className="hero-role-loop hero-role-loop--mobile">
                          {HeroRoles.map((role) => (
                            <span key={role}>{role}</span>
                          ))}
                        </TextLoop>
                      </h1>
                      <h2>based in Lausanne, Switzerland</h2>
                      <div className="hero-actions">
                        <a
                          className="rn-button-style--2 btn-solid portfolio-primary-action"
                          href="#portfolio"
                        >
                          <span>Explore Projects</span>
                          <FiArrowRight />
                        </a>
                        <a
                          className="rn-button-style--2 portfolio-secondary-action"
                          href="#contact"
                        >
                          <span>Start a Project</span>
                        </a>
                        <a
                          className="rn-button-style--2 portfolio-resume-action"
                          href="https://online-resume-olive.vercel.app/resume"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <span>Check Out My Resume</span>
                          <FiExternalLink />
                        </a>
                      </div>
                      <div
                        className="hero-signal-row"
                        aria-label="Current strengths"
                        ref={heroSignalRowRef}
                      >
                        {HeroSignals.map((signal) => {
                          const SignalIcon = signal.icon;
                          return (
                            <div className="hero-signal" key={signal.label}>
                              <SignalIcon />
                              <span>{signal.label}</span>
                              <strong>{signal.value}</strong>
                            </div>
                          );
                        })}
                      </div>
                      {value.description ? (
                        <p className="description">{value.description}</p>
                      ) : (
                        ""
                      )}
                      {value.buttonText ? (
                        <div className="slide-btn">
                          <a
                            className="rn-button-style--2 btn-primary-color"
                            href={`${value.buttonLink}`}
                          >
                            {value.buttonText}
                          </a>
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                  <div className="col-lg-5">
                    <div className="hero-console" aria-label="Current engineering focus">
                      <div className="hero-console-top">
                        <span>live portfolio</span>
                        <div className="hero-console-dots" aria-hidden="true">
                          <span />
                          <span />
                          <span />
                        </div>
                      </div>
                      <div className="hero-console-command">
                        <FiTerminal />
                        <span>ship --with-ai --polished-ui</span>
                      </div>
                      <div className="hero-console-list">
                        {HeroConsoleRows.map((row) => (
                          <div className="hero-console-row" key={row.label}>
                            <span>{row.label}</span>
                            <strong>{row.value}</strong>
                          </div>
                        ))}
                      </div>
                      <div className="hero-console-meter" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <a className="hero-scroll-cue" href="#about" aria-label="Scroll to about section">
                <span className="scroll-cue-mouse" aria-hidden="true">
                  <span />
                </span>
                <span className="scroll-cue-label">Scroll</span>
              </a>
            </div>
          ))}
          {/* End Single Slide */}
        </div>
      </div>
      {/* End Slider Area   */}

      {/* Start About Area */}
      <div id="about" className="fix">
        <div className="about-area portfolio-section portfolio-about-section ptb--120 bg_color--1">
          <div className="about-wrapper">
            <div className="container">
              <div className="row row--35 align-items-start">
                <div className="col-lg-5">
                  <div className="thumbnail about-photo-frame">
                    <img className="w-100" src={about} alt="About Images" />
                  </div>
                </div>
                <div className="col-lg-7">
                  <div className="about-inner inner">
                    <div className="section-title">
                      <span className="section-kicker">Profile</span>
                      <h2 className="title">{title}</h2>
                      <p className="description">{description}</p>
                    </div>
                    <div className="about-highlight-row">
                      {AboutHighlights.map((highlight) => (
                        <div className="about-highlight" key={highlight.number}>
                          <span>{highlight.number}</span>
                          <strong>{highlight.title}</strong>
                          <p>{highlight.text}</p>
                        </div>
                      ))}
                    </div>
                    <div className="row mt--30">
                      <TabTwo tabStyle="tab-style--1" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* End About Area */}

      {/* Start Service Area  */}
      <div id="service" className="fix">
        <div className="service-area creative-service-wrapper portfolio-section portfolio-services-section ptb--120 bg_color--5">
          <div className="container-fluid plr--50 plr_sm--20">
            <div className="row">
              <div className="col-lg-12">
                <div className="section-title text-center service-style--3 mb--30 mb_sm--0">
                  <span className="section-kicker">How I can help</span>
                  <h2 className="title">My Services</h2>
                  <p>
                    I help teams ship polished web products, AI-enabled workflows, interactive 3D experiences, Web3 prototypes, and reliable full-stack systems.
                  </p>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-12">
                <div className="skill-stack-panel">
                  <div className="skill-stack-copy">
                    <span>Skill stack</span>
                    <strong>Tools I use to ship product work</strong>
                  </div>
                  <div className="skill-chip-grid" aria-label="Technical skills">
                    {SkillStack.map((skill) => (
                      <a href="#portfolio" key={skill}>{skill}</a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="row creative-service">
              <div className="col-lg-12">
                <ServiceList
                  item="9"
                  column="col-lg-4 col-md-6 col-sm-6 col-12 text-left"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* End Service Area  */}

      {/* Start Portfolio Area */}
      <div id="portfolio" className="fix">
        <div className="portfolio-area portfolio-section portfolio-showcase-section ptb--120 bg_color--1">
          <div className="portfolio-sacousel-inner">
            <div className="container-fluid plr--50 plr_sm--20">
              <div className="row">
                <div className="col-lg-12">
                  <div className="section-title text-center service-style--3 mb--30 mb_sm--0">
                    <span className="section-kicker">Recent builds</span>
                    <h2 className="title">My Latest Projects</h2>
                    <p>
                      Recent work across AI tooling, art-tech, 3D visualization, Web3 experiments, leadership platforms, and product dashboards.
                    </p>
                  </div>
                </div>
              </div>
              <div className="row">
                <PortfolioList
                  styevariation="text-center mt--40"
                  column="col-lg-4 col-md-6 col-sm-6 col-12"
                  item="9"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* End Portfolio Area */}

      {/* Start Blog Area */}
      <div id="blog" className="fix">
        <div className="rn-blog-area portfolio-section portfolio-older-section ptb--120 bg_color--5 mb-dec--30">
          <div className="container-fluid plr--50 plr_sm--20">
            <div className="row align-items-end">
              <div className="col-lg-12 col-md-12 col-sm-12 col-12">
                <div className="section-title text-center">
                  <span className="section-kicker">Earlier chapters</span>
                  <h2>Older Projects</h2>
                  <p>
                   Earlier projects that shaped my engineering range across enterprise products, healthcare UX, and games.
                    <br />
                    Take a look.
                  </p>
                </div>
              </div>
            </div>
            <div className="row mt--60 mt_sm--40">
              {PostList.map((value, i) => (
                <div className="col-lg-4 col-md-6 col-12" key={i}>
                  <div className="blog blog-style--1 older-project-card">
                    <div className="thumbnail">
                      <a href={value.href}>
                        <img
                          style={{width: '100%', objectFit: 'cover', objectPosition:'center'}}
                          className="w-100"
                          src={value.images}
                          alt="Blog Images"
                        />
                      </a>
                    </div>
                    <div className="content">
                      <h3 className="blogtype">{value.category}</h3>
                      <h6 className="title">
                        <a href={value.href}>{value.title}</a>
                      </h6>
                      <div className="blog-btn">
                        <a className="rn-btn text-white" href={value.href}>
                          Read More
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* End Blog Area */}

      {/* Start COntact Area */}
      <div id="contact" className="fix">
        <div className="rn-contact-area portfolio-section portfolio-contact-section ptb--120 bg_color--1">
          <ContactThree contactImages={contactImg} contactTitle="Build With Me." />
        </div>
      </div>
      {/* End COntact Area */}

      <FooterTwo />
      {/* Start Back To Top */}
      <div className="backto-top">
        <ScrollToTop showUnder={160}>
          <FiChevronUp />
        </ScrollToTop>
      </div>
      {/* End Back To Top */}
    </div>
  );
};

export default PortfolioLanding;
